import { createTheme } from '@mui/material/styles';

// Unified product theme for educators
// Palette per spec:
// - Primary Blue: #2563EB
// - Primary Orange: #F97316
// - Gradient Midpoint: #8B5CF6
// - Background: #F9FAFB
// - Text Primary: #111827, Secondary: #6B7280
// - Success/Accent Green: #10B981
const colors = {
  primary: {
    main: '#2563EB',
    light: '#3B82F6',
    dark: '#1E40AF',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#F97316',
    light: '#FB923C',
    dark: '#C2410C',
    contrastText: '#ffffff',
  },
  background: {
    default: '#F9FAFB',
    paper: '#ffffff',
  },
  text: {
    primary: '#111827',
    secondary: '#6B7280',
    disabled: '#9CA3AF',
  },
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#B91C1C',
  },
  info: {
    main: '#3B82F6',
    light: '#93C5FD',
    dark: '#1D4ED8',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#B45309',
  },
  custom: {
    gradientPrimary: 'linear-gradient(90deg, #2563EB 0%, #8B5CF6 50%, #F97316 100%)',
  },
  neutral: {
    cardBorder: '#E5E7EB',
    cardBorderHover: '#D1D5DB',
    divider: '#F3F4F6',
    surface: '#F9FAFB',
  },
};

// Create the theme with adjusted spacing, typography, and enhanced box shadows
// Box shadows use layered approach (primary + ambient) for better visual depth
// Spacing system: 8px base unit for consistent design (8, 16, 24, 32, 40px etc.)
const appTheme = createTheme({
  palette: colors as any,
  typography: {
    fontFamily: '"Inter", "Nunito", "Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 16,
    h1: {
      fontSize: '2.25rem', // 36px
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.75rem', // 28px
      fontWeight: 700,
      lineHeight: 1.25,
    },
    h3: {
      fontSize: '1.5rem', // 24px
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.25rem', // 20px
      fontWeight: 600,
      lineHeight: 1.35,
    },
    body1: {
      fontSize: '1rem', // 16px
      lineHeight: 1.65,
      fontWeight: 500,
    },
    body2: {
      fontSize: '0.95rem',
      lineHeight: 1.6,
    },
    button: {
      fontSize: '0.95rem',
      textTransform: 'none',
      fontWeight: 700,
    },
  },
  spacing: 8, // 8px base unit for consistent spacing throughout the app
  shape: {
    borderRadius: 8, // Slightly increased for a more modern look
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '8px 16px', // 8px multiple
          minHeight: '40px', // 8px multiple (5 * 8px)
          boxShadow: '0 2px 6px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px) scale(1.03)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15), 0 3px 6px rgba(0,0,0,0.10)',
            filter: 'brightness(1.05)',
          },
        },
        sizeLarge: {
          padding: '16px 24px', // 8px multiples
          minHeight: '48px', // 8px multiple (6 * 8px)
        },
        sizeSmall: {
          padding: '8px 16px', // 8px multiples
          minHeight: '32px', // 8px multiple (4 * 8px)
        },
        // Primary CTA uses gradient across brand colors
        containedPrimary: {
          background: colors.custom.gradientPrimary,
        },
        // Secondary appears as white with primary border
        outlinedPrimary: {
          backgroundColor: '#ffffff',
          borderColor: colors.primary.main,
          color: colors.primary.main,
          '&:hover': {
            backgroundColor: 'rgba(37, 99, 235, 0.06)',
            borderColor: colors.primary.dark,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
          transition: 'box-shadow 0.3s ease-in-out',
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.neutral.cardBorder}`,
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)',
            borderColor: colors.neutral.cardBorderHover,
          },
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
          border: `1px solid ${colors.neutral.cardBorder}`,
        },
        elevation2: {
          boxShadow: '0 3px 12px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.06)',
          border: `1px solid ${colors.neutral.cardBorder}`,
        },
        elevation3: {
          boxShadow: '0 6px 20px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)',
          border: `1px solid ${colors.neutral.cardBorder}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)',
          border: `1px solid ${colors.neutral.cardBorder}`,
          backgroundColor: colors.background.paper,
          '&:hover': {
            transform: 'translateY(-4px) scale(1.02)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.10)',
            borderColor: colors.neutral.cardBorderHover,
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          paddingBottom: '8px', // 8px multiple
        },
        title: {
          fontWeight: 600,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '16px', // 8px multiple (2 * 8px)
          '&:last-child': {
            paddingBottom: '16px', // 8px multiple
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            fontSize: '0.95rem',
            borderRadius: '8px',
            transition: 'box-shadow 0.2s ease-in-out',
            '&.Mui-focused': {
              boxShadow: '0 0 0 3px rgba(41, 98, 255, 0.1)',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '0.95rem',
          borderRadius: '8px',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
          },
        },
        input: {
          padding: '16px', // 8px multiple (2 * 8px)
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.95rem',
          fontWeight: 500,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.95rem',
          minHeight: '40px', // 8px multiple (5 * 8px)
          paddingTop: '8px', // 8px multiple
          paddingBottom: '8px', // 8px multiple
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: '8px', // 8px multiple
          transition: 'all 0.2s ease-in-out',
          borderRadius: '8px', // 8px multiple
          '&:hover': {
            transform: 'translateX(4px) scale(1.01)',
            backgroundColor: 'rgba(0,0,0,0.06)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          borderRadius: '8px', // 8px multiple
          padding: '8px 16px', // 8px multiples
          '&:hover': {
            transform: 'translateX(4px) scale(1.01)',
            backgroundColor: 'rgba(0,0,0,0.06)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '16px', // 8px multiple (2 * 8px)
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '16px', // 8px multiple (2 * 8px)
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
          padding: '8px 16px', // 8px multiples
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.05)',
        },
        standardSuccess: {
          backgroundColor: 'rgba(76, 175, 80, 0.12)',
          color: '#1B5E20', // WCAG AA compliant dark green
          borderColor: 'rgba(76, 175, 80, 0.15)',
        },
        standardError: {
          backgroundColor: 'rgba(244, 67, 54, 0.12)',
          color: '#B71C1C', // WCAG AA compliant dark red
          borderColor: 'rgba(244, 67, 54, 0.15)',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 193, 7, 0.12)',
          color: '#E65100', // WCAG AA compliant dark orange
          borderColor: 'rgba(255, 193, 7, 0.15)',
        },
        standardInfo: {
          backgroundColor: 'rgba(0, 188, 212, 0.12)',
          color: '#006064', // WCAG AA compliant dark teal
          borderColor: 'rgba(0, 188, 212, 0.15)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.2rem',
          fontWeight: 600,
          padding: '16px 24px', // 8px multiples
          backgroundColor: colors.neutral.surface,
          color: colors.text.primary,
          borderBottom: `1px solid ${colors.neutral.cardBorder}`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px', // 8px multiples
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px', // 8px multiple (2 * 8px)
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '16px', // 8px multiple (2 * 8px)
          fontSize: '0.9rem',
        },
        head: {
          fontSize: '0.9rem',
          fontWeight: 600,
          backgroundColor: colors.neutral.surface,
          color: colors.text.primary,
          borderBottom: `2px solid ${colors.neutral.cardBorder}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: colors.neutral.surface,
          },
          '&:hover': {
            backgroundColor: colors.neutral.cardBorder,
          },
          borderBottom: `1px solid ${colors.neutral.divider}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px', // 8px multiple (2 * 8px)
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            filter: 'brightness(1.05)',
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
        },
        li: {
          '&:last-child': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          padding: '8px', // 8px multiple
          '&:hover': {
            transform: 'scale(1.1)',
            backgroundColor: 'rgba(0,0,0,0.08)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          margin: '16px 0', // 8px multiple (2 * 8px)
        },
      },
    },
  },
});

export default appTheme; 